import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function canPrecompute(file, maxPrecomputeBytes) {
  if (!file) return false;
  if (typeof Worker === 'undefined') return false;
  if ((file.size ?? 0) > maxPrecomputeBytes) return false;
  return true;
}

export function usePrecomputedSave({
  file,
  edits,
  annotations,
  formValues,
  options,
  compute,
  debounceMs = 500,
  maxPrecomputeBytes = 20_000_000,
}) {
  const enabled = canPrecompute(file, maxPrecomputeBytes);

  const inputs = useMemo(() => ({ file, edits, annotations, formValues, options, compute }),
    [file, edits, annotations, formValues, options, compute]);
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;

  const [state, setState] = useState(enabled ? 'idle' : 'disabled');
  const tokenRef = useRef(0);
  const timerRef = useRef(null);
  const inflightRef = useRef(null); // { promise, token }
  const bytesRef = useRef(null);
  const mountedRef = useRef(true);
  const firstEffectRef = useRef(true);

  // Empty deps is intentional: startCompute reads everything through refs
  // (inputsRef, tokenRef, mountedRef) so its identity stays stable and the
  // input-change effect doesn't re-fire on every render. Any direct closure
  // over inputs would silently go stale — keep reading through refs.
  const startCompute = useCallback(() => {
    const token = ++tokenRef.current;
    const snap = inputsRef.current;
    setState('computing');
    bytesRef.current = null;

    const promise = (async () => {
      const arrayBuffer = await snap.file.arrayBuffer();
      return snap.compute({
        arrayBuffer,
        edits: snap.edits,
        annotations: snap.annotations,
        formValues: snap.formValues,
        options: snap.options,
      });
    })();

    inflightRef.current = { promise, token, inputs: snap };

    // Defer the terminal state transition by one macrotask so tests (and
    // callers) can observe the 'computing' state between the debounce firing
    // and the compute promise resolving. Microtasks flush inside
    // `await act(async () => { vi.advanceTimersByTime(...) })`, which would
    // otherwise collapse the two transitions into a single render.
    promise.then(
      (bytes) => {
        setTimeout(() => {
          if (!mountedRef.current) return;
          // Free our reference so the promise can GC. Use token identity — a newer
          // startCompute may have overwritten inflightRef by the time we run.
          if (inflightRef.current && inflightRef.current.token === token) {
            inflightRef.current = null;
          }
          if (tokenRef.current !== token) return; // stale — don't touch bytesRef or state
          bytesRef.current = { bytes, inputs: snap };
          setState('ready');
        }, 0);
      },
      () => {
        setTimeout(() => {
          if (!mountedRef.current) return;
          // Free our reference so the promise can GC. Use token identity — a newer
          // startCompute may have overwritten inflightRef by the time we run.
          if (inflightRef.current && inflightRef.current.token === token) {
            inflightRef.current = null;
          }
          if (tokenRef.current !== token) return; // stale — don't touch state
          setState('idle');
        }, 0);
      }
    );

    return promise;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState('disabled');
      return;
    }
    if (firstEffectRef.current) {
      firstEffectRef.current = false;
      return;
    }
    tokenRef.current += 1;
    bytesRef.current = null;
    setState('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      startCompute();
    }, debounceMs);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [file, edits, annotations, formValues, options, compute, enabled, debounceMs, startCompute]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const getSavedBytes = useCallback(async () => {
    const snap = inputsRef.current;
    if (!enabled) {
      const arrayBuffer = await snap.file.arrayBuffer();
      return snap.compute({
        arrayBuffer,
        edits: snap.edits,
        annotations: snap.annotations,
        formValues: snap.formValues,
        options: snap.options,
      });
    }
    // State effects run after render; only reuse bytes for this exact revision.
    if (bytesRef.current?.inputs === snap) return bytesRef.current.bytes;
    if (inflightRef.current?.inputs === snap) return inflightRef.current.promise;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return startCompute();
  }, [state, enabled, startCompute]);

  return useMemo(
    () => ({ getSavedBytes, state: enabled ? state : 'disabled' }),
    [getSavedBytes, state, enabled]
  );
}
